import { Button, Box, Icon, Switch, Text, Tooltip } from "@showitinc/showit-wc";

import React from "react";
import cx from "classnames";
import AppConstants from "../constants/AppConstants";
import UIActions from "../actions/UIActions";
import PreviewStore from "../stores/PreviewStore";
import PreviewPanel from "./PreviewPanel";
import PreviewPanelFooter from "./PreviewPanelFooter";
import SubviewError from "./SubviewError";
import SubviewProcessing from "./SubviewProcessing";
import Modal from "./base/Modal";
import { useSiteState } from "../hooks/query/useSite";
import { getBlogType } from "../hooks/query/useAccount";
import DesignSaveStore from "../stores/DesignSaveStore";
import styles from "./PreviewPanelModal.module.css";
import BlogPreviewButton from "./BlogPreviewButton";
import UserActions from "../actions/UserActions";
import useStoreState from "./providers/useStoreState";
import { LAYOUT_DESKTOP, LAYOUT_MOBILE } from "@/constants/DesignConstants.mjs";
import HelpTooltip from "./HelpTooltip";
import useRenderer from "@/hooks/query/useRenderer";
import UpgradedIcon from "./UpgradedIcon";

const {
  BLOG_TYPE_NONE,
  PREVIEW_VIEW_SAVING,
  PREVIEW_VIEW_PROCESSING,
  PREVIEW_VIEW_ERROR,
  PREVIEW_VIEW_COMPLETE,
} = AppConstants;

const SPLIT_PREVIEW_URL = "https://split-view.tools.tiwohs.com";

/**
 * @typedef {import("@/stores/PreviewStore").PreviewSettings} PreviewSettings
 * @typedef {typeof LAYOUT_DESKTOP | typeof LAYOUT_MOBILE} Layouts
 */

/**
 * Layout toggle button class, with the active modifier applied when selected.
 * @param {boolean} active
 * @returns {string}
 */
const layoutBtn = (active) =>
  cx(styles["layout-btn"], { [styles["layout-btn-active"]]: active });

/**
 * Renders the preview modal
 * @param {Object} props
 * @param {Layouts} props.activeTab
 * @param {() => void} props.onHide
 * @returns {React.JSX.Element}
 */
const PreviewPanelModal = ({ activeTab, onHide, ...other }) => {
  const site = useSiteState();
  const siteId = site.get("site_design_id");
  const visible = siteId != null;

  /** @type {PreviewSettings} */
  const data = useStoreState(PreviewStore, PreviewStore.getState);
  const { canToggle, settled: rendererSettled } = useRenderer(siteId, {
    enabled: visible,
  });
  const isUpgraded = rendererSettled && !canToggle;

  /**
   * Closes the preview modal
   */
  const _closePreview = () => {
    UIActions.previewClose();
  };

  /**
   * Changes layout selection if supplied layout doesn't match current
   * @param {Layouts} layout
   */
  const _selectLayout = (layout) => {
    if (activeTab !== layout) {
      UIActions.selectActiveLayout({ layout, from: "panel" });
    }
  };

  const _openSplitPreview = () => {
    const url = new URL(SPLIT_PREVIEW_URL);
    const previewUrl = data.previewURL.replace(/^[^:]*?(:\/\/)/, "");

    url.searchParams.set("left", previewUrl);
    window.open(url, "_blank");
    UserActions.trackPostHogEvent("split_preview_clicked", {
      preview_url: previewUrl,
    });
  };

  /**
   * Toggles protorenderer usage
   * @param {CustomEvent} event
   */
  const _toggleProtorenderer = (event) => {
    const useProtorenderer = event?.detail?.value ?? false;
    UIActions.toggleProtorenderer(useProtorenderer);
    UserActions.trackPostHogEvent("protorenderer_toggled", {
      enabled: useProtorenderer,
    });
  };

  /**
   * Renders preview modal header content
   * @param {PreviewSettings} props
   * @returns {React.JSX.Element}
   */
  const _getPreviewHeader = ({ previewURL, blogPreviewUrl }) => {

    const canGeneratePreview = DesignSaveStore.getState();

    return (
      <Modal.Dialog.Title className={styles.title}>
        <div className={styles.content}>
          <div className={styles.left}>
            <Box className={styles.titleText}>Site Preview</Box>
            <a
              href={previewURL}
              target="_blank"
              rel="noreferrer"
              className={styles["header-link"]}
            >
              <Icon
                type="openNewTab"
                size="16"
                className={styles["header-link-icon"]}
              />
              Open in new tab
            </a>
            {getBlogType() !== BLOG_TYPE_NONE &&
              canGeneratePreview && (
                <BlogPreviewButton blogPreviewUrl={blogPreviewUrl} />
              )}
          </div>

          {canToggle && (
            <div className={styles.center}>
              <Switch
                className={cx(styles["protorenderer-toggle"], {
                  [styles["protorenderer-toggle-active"]]:
                    data.useProtorenderer,
                })}
                controlled
                checked={data.useProtorenderer}
                label="Preview Beta Publish Engine"
                onSIChange={_toggleProtorenderer}
              />
              <HelpTooltip
                className={styles["protorenderer-info"]}
                tooltipKey="protorenderer-info"
                placement="bottom-center"
              ></HelpTooltip>
              <Tooltip position="bottom-center">
                <span slot="content">
                  <Text className={styles["split-preview-text"]}>
                    Click here for a side-by-side view of your design. Showit’s
                    new Publish Engine will display on the right side of this
                    split view.
                  </Text>
                </span>
                <Button
                  variant="icon"
                  icon-prefix="splitScreen"
                  onSIClick={_openSplitPreview}
                >
                  Split Preview
                </Button>
              </Tooltip>
            </div>
          )}

          {isUpgraded && (
            <div className={styles.center}>
              <div className={styles["upgraded-indicator"]}>
                <UpgradedIcon className={styles["upgraded-indicator-icon"]} />
                <Text>Previewing with latest version of Showit</Text>
              </div>
            </div>
          )}
        </div>
      </Modal.Dialog.Title>
    );
  };

  /**
   * Renders header title
   * @returns {React.JSX.Element}
   */
  const _getHeader = () => (
    <Modal.Dialog.Title>Site Preview</Modal.Dialog.Title>
  );

  /**
   * Renders preview footer contents
   * @param {PreviewSettings} data
   * @returns {React.JSX.Element}
   */
  const _getPreviewFooter = (data) => {
    const { selectedLayout } = data;
    const mobileButtonActive = selectedLayout === LAYOUT_MOBILE;
    const desktopButtonActive = selectedLayout === LAYOUT_DESKTOP;

    return (
      <PreviewPanelFooter>
        <Button
          className={layoutBtn(mobileButtonActive)}
          variant="minimal"
          size="lg"
          iconPrefix="mobileDevice"
          iconSize="20"
          onClick={() => _selectLayout(LAYOUT_MOBILE)}
        >
          Mobile
        </Button>
        <Button
          className={layoutBtn(desktopButtonActive)}
          variant="minimal"
          size="lg"
          iconPrefix="desktopDevice"
          iconSize="20"
          onClick={() => _selectLayout(LAYOUT_DESKTOP)}
        >
          Desktop
        </Button>
      </PreviewPanelFooter>
    );
  };

  /**
   * Renders preview modal contents
   * @param {PreviewSettings} data
   * @returns
   */
  const _getPreviewBody = (data) => (
    <PreviewPanel selectedLayout={data.selectedLayout} url={data.pageURL} />
  );

  /**
   * Renders preview error content
   * @param {PreviewSettings} data
   * @returns {React.JSX.Element}
   */
  const _getErrorBody = (data) => {
    const { error } = data.process;

    return <SubviewError error={error} onClose={_closePreview} />;
  };

  /**
   * Renders processing message
   * @param {string} message
   * @returns {React.JSX.Element}
   */
  const _getProcessingBody = (message) => (
    <SubviewProcessing message={message} />
  );

  let header = null;
  let body = null;
  let footer = null;

  switch (data?.process?.view) {
    case PREVIEW_VIEW_SAVING:
      header = _getHeader();
      body = _getProcessingBody("Your site is saving");
      break;
    case PREVIEW_VIEW_PROCESSING:
      header = _getHeader();
      body = _getProcessingBody("Preview is generating");
      break;
    case PREVIEW_VIEW_ERROR:
      header = _getHeader();
      body = _getErrorBody(data);
      break;
    case PREVIEW_VIEW_COMPLETE:
      header = _getPreviewHeader(data);
      body = _getPreviewBody(data);
      footer = _getPreviewFooter(data);
      break;
    default:
      break;
  }

  return (
    <Modal {...other} onHide={_closePreview}>
      <Modal.Dialog size="large" contentClassName={styles["preview-dialog"]}>
        <Modal.Dialog.Header closeButton className={styles["preview-header"]}>
          {header}
        </Modal.Dialog.Header>
        <Modal.Dialog.Body style={{ padding: 0 }} key={data.selectedLayout}>
          {body}
        </Modal.Dialog.Body>
        <Modal.Dialog.Footer
          className={footer ? styles["preview-footer"] : undefined}
        >
          {footer}
        </Modal.Dialog.Footer>
      </Modal.Dialog>
    </Modal>
  );
};

export default PreviewPanelModal;
